/* eslint-disable react-hooks/exhaustive-deps */
import { CloseOutlined } from '@ant-design/icons'
import { Checkbox, Input, Modal, notification, Select } from 'antd'
import { Rule } from 'antd/es/form'
import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import { useUser } from '@contexts'

import useGetAssignmentZones from '../../../hooks/useGetAssignmentZones'
import { useCreateDepot } from '../../../hooks/useCreateDepot'
import useGetSapGeo from '../../../hooks/useGetSapGeo'
import { Coordinates } from '../../../types'
import MapComponent from '../../Map'
import { StyledForm, StyledRow } from '../../StyledComponents'

type Props = {
  selectedSupplier: number
  onClose: () => void
}

type SapGeoType = {
  id: string | number
  cityCode: string
  description: string
  region: string
  country: string
}

const commonValidations = {
  min: 3,
  max: 50,
}

const formValidations = {
  tag: { ...commonValidations },
  street: { ...commonValidations },
  number: { max: 15 },
}

const AddDepotModal: React.FC<Props> = ({ selectedSupplier, onClose }) => {
  const [form] = StyledForm.useForm()
  const { state: user } = useUser()
  const companyShortName = user?.company?.shortName
  const { resetFields } = form
  const { t } = useTranslation()
  const { sapGeos, sapGeoIsLoading } = useGetSapGeo(companyShortName)
  const { zones, assignmentZoneIsLoading } = useGetAssignmentZones()
  const [city, setCity] = useState('')
  const [state, setState] = useState('')
  const [country, setCountry] = useState('')
  const [defaultCenter, setDefaultCenter] = useState<{ lat: number; lng: number }>()
  const [coordinates, setCoordinates] = useState<Coordinates[] | undefined>()
  const [isNoNumber, setIsNoNumber] = useState(false)
  const [, setFilteredSapGeos] = useState<SapGeoType[] | undefined>(undefined)
  const [fullAddress, setFullAddress] = useState('')
  const resetModal = () => {
    resetFields()
    setCity('')
    setState('')
    setCountry('')
    setCoordinates(undefined)
    onClose()
  }

  const updateMapFromForm = (data: string) => {
    const { street, number } = form.getFieldsValue(['street', 'number'])
    if (!street || (!isNoNumber && !number)) return

    const address = [street + (isNoNumber ? '' : ` ${number}`), data ?? null, state, country]
      .filter(Boolean)
      .join(', ')
    setFullAddress(address)
  }

  useEffect(() => {
    if (user?.company) {
      const center = { lat: user.company.latitude, lng: user.company.longitude }
      setDefaultCenter(center)
    }
  }, [user, sapGeos, selectedSupplier])

  useEffect(() => {
    if (sapGeos && user?.company) {
      const filtered = sapGeos.filter(sapGeo => sapGeo.country === country)
      setFilteredSapGeos(filtered)
      const _filteredSapGeos = sapGeos?.filter(sapGeo => user.company.shortName === sapGeo.country)
      setFilteredSapGeos(_filteredSapGeos)
    }
  }, [sapGeos, country])

  const { createDepot } = useCreateDepot(resetModal)

  const handleCheckboxChange = (e: any) => {
    const checked = e.target.checked
    setIsNoNumber(checked)
    form.setFieldsValue({ number: checked ? null : undefined })
  }

  const validateLocation = (_rule: Rule, value: any) => {
    if (!value) {
      return Promise.reject(t('globals.obligatory_field'))
    }
    return Promise.resolve()
  }

  const validationRules = {
    required: () => ({
      required: true,
      message: t('globals.obligatory_field'),
    }),
    minLength: (min: number) => ({
      min,
      message: t('depot_modal.min_length_error', { min }),
    }),
    maxLength: (max: number) => ({
      max,
      message: t('depot_modal.max_length_error', { max }),
    }),
    numeric: () => ({
      pattern: /^[0-9]+$/,
      message: t('depot_modal.numeric_error'),
    }),
  }

  const ModalTitle = styled.div`
    margin-bottom: 20px;
  `

  const onOk = useCallback(() => {
    form
      .validateFields()
      .then((values: any) => {
        if (!coordinates?.length) {
          notification.error({ message: t('depot_modal.coordinates_required') })
          throw new Error('Coordinates are required')
        }
        const { sapGeo, assignmentZone, number, ...rest } = values

        createDepot({
          ...rest,
          number: isNoNumber ? 'S/N' : number,
          country,
          city,
          state,
          sapGeo: { id: sapGeo },
          assignmentZoneId: assignmentZone,
          location: { type: 'Point', coordinates: [coordinates[0].lat, coordinates[0].lng] }, // TODO: Va harcodeado de type
        })
      })
      .catch(console.error)
  }, [country, city, state, coordinates])

  const handleNumericKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    const charCode = event.charCode
    if (charCode < 48 || charCode > 57) {
      event.preventDefault()
    }
  }

  const handleAddressSelect = (address: {
    city?: string
    state?: string
    country?: string
    street?: string
    number?: string
  }) => {
    setCity(address.city ?? '')
    setState(address.state ?? '')
    setCountry(address.country ?? '')

    setIsNoNumber(!address.number || address.number === '&')

    form.setFieldsValue({
      city: address.city ?? '',
      state: address.state ?? '',
      country: address.country ?? '',
      street: address.street ?? '',
      number: address.number ?? '',
    })
  }

  return (
    <Modal
      open
      width={1000}
      keyboard
      maskClosable
      onCancel={resetModal}
      title={<ModalTitle>{t('depot_modal.title')}</ModalTitle>}
      closeIcon={<CloseOutlined />}
      okText={t('depot_modal.button_add')}
      cancelText={t('depot_modal.button_cancel')}
      destroyOnClose
      onOk={onOk}
    >
      <div style={{ display: 'flex', alignItems: 'stretch', gap: '16px' }}>
        <div style={{ flex: 1 }}>
          <StyledForm form={form} initialValues={{ supplierId: selectedSupplier }}>
            <StyledForm.Item hidden name="supplierId" label={t('depot_modal.supplier_id')}>
              <Input type="number" />
            </StyledForm.Item>

            <StyledRow className="carrier-form-row">
              <StyledForm.Item
                name="tag"
                label={t('depot_modal.name')}
                rules={[
                  validationRules.required(),
                  validationRules.minLength(formValidations.tag.min),
                  validationRules.maxLength(formValidations.tag.max),
                ]}
                hasFeedback
              >
                <Input />
              </StyledForm.Item>
            </StyledRow>

            <StyledRow className="carrier-form-row">
              <StyledForm.Item
                name="street"
                label={t('depot_modal.street')}
                rules={[
                  validationRules.required(),
                  validationRules.minLength(formValidations.street.min),
                  validationRules.maxLength(formValidations.street.max),
                ]}
                hasFeedback
              >
                <Input />
              </StyledForm.Item>
            </StyledRow>

            <StyledRow className="carrier-form-row" style={{ marginBottom: 8 }}>
              <>
                <StyledForm.Item
                  name="number"
                  label={t('depot_modal.number')}
                  rules={!isNoNumber ? [validationRules.required(), validationRules.numeric()] : []}
                  hasFeedback
                  style={{ flex: 1, marginBottom: 0 }}
                >
                  <Input type="number" onKeyPress={handleNumericKeyPress} disabled={isNoNumber} />
                </StyledForm.Item>

                <Checkbox
                  onChange={handleCheckboxChange}
                  checked={isNoNumber}
                  style={{ marginLeft: 2 }}
                >
                  {t('depot_modal.no_number')}
                </Checkbox>
              </>
            </StyledRow>

            {/* <StyledRow className="carrier-form-row">
              <StyledForm.Item
                name="number"
                label={t('depot_modal.number')}
                rules={!isNoNumber ? [validationRules.required(), validationRules.numeric()] : []}
                hasFeedback
                style={{ flex: 1, minWidth: '80px' }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Checkbox
                    onChange={handleCheckboxChange}
                    checked={isNoNumber}
                    style={{ flex: '0 0 20%' }}
                  >
                    {t('depot_modal.no_number')}
                  </Checkbox>
                  {!isNoNumber && (
                    <Input type="number" onKeyPress={handleNumericKeyPress} style={{ flex: '1' }} />
                  )}
                </div>
              </StyledForm.Item>
            </StyledRow> */}

            <StyledRow className="carrier-form-row">
              <StyledForm.Item
                name="assignmentZone"
                label={t('depot_modal.assigmemnt_zone')}
                hasFeedback
              >
                <Select
                  className="custom-select"
                  showSearch
                  allowClear
                  loading={assignmentZoneIsLoading}
                  filterOption={(input, option) => {
                    const combined = option?.props['data-combined'] ?? ''
                    return combined.toLowerCase().includes(input.toLowerCase())
                  }}
                  optionLabelProp="children"
                >
                  {zones?.map(item => (
                    <Select.Option
                      key={item.id}
                      value={item.id}
                      data-combined={`${item.name} ${item.code}`}
                    >
                      {`${item.name} (${item.code})`}
                    </Select.Option>
                  ))}
                </Select>
              </StyledForm.Item>
            </StyledRow>

            <StyledRow className="carrier-form-row">
              <StyledForm.Item
                name="sapGeo"
                label={t('depot_modal.location')}
                rules={[{ validator: validateLocation }]}
                required
                hasFeedback
              >
                <Select
                  className="custom-select"
                  showSearch
                  loading={sapGeoIsLoading}
                  filterOption={(input, option) => {
                    const combined = option?.props['data-combined'] ?? ''
                    return combined.toLowerCase().includes(input.toLowerCase())
                  }}
                  optionLabelProp="children"
                  onChange={value => {
                    const selectedSapGeo = sapGeos?.find(sapGeo => sapGeo.id === value)
                    setCountry(selectedSapGeo?.country ?? '')
                    setCity(selectedSapGeo?.description ?? '')
                    setState(selectedSapGeo?.region ?? '')
                    setTimeout(() => {
                      updateMapFromForm(selectedSapGeo?.description ?? '')
                    }, 300)
                  }}
                  style={{ width: '100%' }}
                  dropdownStyle={{ width: 400 }}
                >
                  {sapGeos?.map(item => (
                    <Select.Option
                      key={item.id}
                      value={item.id}
                      data-combined={`${item.cityCode} ${item.description} ${item.region}`}
                    >
                      {`${item.cityCode} - ${item.description}, ${item.region}`}
                    </Select.Option>
                  ))}
                </Select>
              </StyledForm.Item>
            </StyledRow>

            <StyledRow className="carrier-form-row">
              <StyledForm.Item label={t('depot_modal.city')}>
                <span className="ticket-field-value">{city}</span>
              </StyledForm.Item>
            </StyledRow>

            <StyledRow className="carrier-form-row">
              <StyledForm.Item label={t('depot_modal.province')}>
                <span className="ticket-field-value">{state}</span>
              </StyledForm.Item>
            </StyledRow>

            <StyledRow className="carrier-form-row">
              <StyledForm.Item label={t('depot_modal.country')}>
                <span className="ticket-field-value">{country}</span>
              </StyledForm.Item>
            </StyledRow>
          </StyledForm>
        </div>
        <div style={{ width: '1px', backgroundColor: '#ccc' }} />
        <div style={{ flex: 1 }}>
          <span>{t('depot_modal.select_location')}</span>
          <MapComponent
            coordinates={coordinates}
            setCoordinates={setCoordinates}
            defaultCenter={defaultCenter}
            editable={true}
            fullAddress={fullAddress}
            onAddressSelect={handleAddressSelect}
          />
        </div>
      </div>
    </Modal>
  )
}

export { AddDepotModal }
